import React from 'react';
import {
  Outlet,
  createRootRoute,
  useRouterState,
} from '@tanstack/react-router';
import { init as initialiseSentry } from '@sentry/react';

import { UniversalTanstackRouterLink } from '@reckon-web/universal-tanstack-router-link';
import { LoadScript } from '@reckon-web/load-script';
import { RestApiClientProvider } from '@reckon-web/rest-api-client';
import { ErrorBoundaryProvider } from '@reckon-web/error-boundary';
import { LoadGoogleTagManagerService } from '@reckon-web/google-tag-manager';
import { LoadBranchIoService } from '@reckon-web/branchio';
import { ReckonBalanceCore } from '@reckon-web/reckon-balance-core';

import { BookIdProvider } from '../services/Book/BookIdProvider';
import { PermissionsProvider } from '../services/Permissions';
import { SiteRoutesProvider } from '../services/Navigation';
import { CompanyIdFromRouteProvider } from '../services/Company';
import { PortalAuthProvider } from '../services/Auth';
import { PortalApolloProvider } from '../services/PortalGqlApi';
import { PageBlockProvider } from '../components/generic/PageBlock/PageBlockProvider';
import { Config } from '../services/Config';

initialiseSentry({
  dsn: Config.APPLICATION_SENTRY_DSN,
  environment: Config.STAGE,
  release: Config.APPLICATION_VERSION,
});

(async () => {
  if ('undefined' === typeof window) {
    return;
  }

  LoadScript({
    parent: 'head',
    id: `newrelic-${Config.STAGE}`,
    src: `/public/newrelic-${Config.STAGE}.js`,
  });
  LoadGoogleTagManagerService({
    apiKey: Config.APPLICATION_GOOGLE_GTM_APIKEY,
  });
  LoadBranchIoService({
    apiKey: Config.APPLICATION_BRANCHIO_APIKEY,
  });
})();

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const pathname = useRouterState({
    select: (state) => {
      return state.location.pathname;
    },
  });

  return (
    <ReckonBalanceCore
      linkComponent={UniversalTanstackRouterLink}
      themeMode="light"
    >
      <ErrorBoundaryProvider onError={console.error}>
        <PortalAuthProvider>
          <PortalApolloProvider>
            <RestApiClientProvider>
              <CompanyIdFromRouteProvider>
                <BookIdProvider>
                  <PermissionsProvider>
                    <SiteRoutesProvider currentPath={pathname}>
                      <PageBlockProvider size="large">
                        <Outlet />
                      </PageBlockProvider>
                    </SiteRoutesProvider>
                  </PermissionsProvider>
                </BookIdProvider>
              </CompanyIdFromRouteProvider>
            </RestApiClientProvider>
          </PortalApolloProvider>
        </PortalAuthProvider>
      </ErrorBoundaryProvider>
    </ReckonBalanceCore>
  );
}
